import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import GridLayout, { WidthProvider, type Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './analytics.css';
import {
  AlertTriangle,
  BarChart3,
  Check,
  GaugeCircle,
  GripVertical,
  Info,
  LayoutGrid,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Table2,
  X,
} from 'lucide-react';
import {
  CATEGORY_LABELS,
  getDefaultWidgetsForPersona,
  getWidgetDefinition,
  PERSONA_KEYS,
  PERSONA_LABELS,
  WIDGET_REGISTRY,
  type PersonaKey,
  type WidgetCategory,
  type WidgetDefinition,
} from './widgetRegistry.tsx';
import { PageHeader, SectionHeader, WidgetFrameProvider } from './controller/shared.tsx';
import { AnalyticsTabs } from './AnalyticsTabs.tsx';
import { getPreviewSeed } from './previewSampleData.ts';
import type { WidgetInstance } from '../../request/dashboards/layout.ts';
import {
  useDashboardLayoutQuery,
  useResetDashboardLayout,
  useSaveDashboardLayout,
} from '../../hooks/dashboards/useDashboardLayout.ts';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';

const ResponsiveGridLayout = WidthProvider(GridLayout);

const GRID_COLS = 12;
const ROW_HEIGHT = 36;
const SAVE_DEBOUNCE_MS = 900;
const CATEGORY_ORDER: WidgetCategory[] = ['kpi', 'chart', 'table'];

const CATEGORY_STYLES: Record<WidgetCategory, { icon: React.ComponentType<{ className?: string }>; badgeClass: string }> = {
  kpi: { icon: GaugeCircle, badgeClass: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  chart: { icon: BarChart3, badgeClass: 'bg-violet-50 text-violet-700 ring-violet-600/20' },
  table: { icon: Table2, badgeClass: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
};

/** Small color-coded KPI/Chart/Table badge — used in the gallery and on each placed tile's drag handle. */
const CategoryBadge: React.FC<{ category: WidgetCategory; className?: string }> = ({ category, className = '' }) => {
  const { icon: Icon, badgeClass } = CATEGORY_STYLES[category];
  return (
    <span
      className={`analytics-category-chip inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ring-inset ${badgeClass} ${className}`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {CATEGORY_LABELS[category]}
    </span>
  );
};

/**
 * Catches a render error thrown by a live widget preview so one broken
 * report never takes down the whole gallery drawer — falls back to a
 * category icon/thumbnail, per the "attempt live preview, degrade to a
 * representative icon" contract for the widget gallery.
 */
class PreviewErrorBoundary extends React.Component<
  { category: WidgetCategory; children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      const { icon: Icon } = CATEGORY_STYLES[this.props.category];
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-slate-300">
          <Icon className="h-7 w-7" aria-hidden />
          <span className="text-[10px] font-medium text-slate-400">Preview unavailable</span>
        </div>
      );
    }
    return this.props.children;
  }
}

const PREVIEW_HEIGHT_PX = 108;
const PREVIEW_SCALE = 0.42;

/**
 * Live, non-interactive miniature render of the actual widget — the gallery
 * card renders the real `Component` at full size inside a `pointer-events:
 * none`, `overflow: hidden` box and scales it down with a CSS transform, so
 * what the user sees is a true preview of the widget they're about to add
 * (not a mockup). Falls back to a category icon via `PreviewErrorBoundary`
 * if a given widget's render throws.
 *
 * Data source: the preview is wrapped in its OWN isolated `QueryClient`,
 * pre-seeded (via `previewSampleData.ts`) with randomized sample data under
 * the widget's real query key — so the widget's own hook resolves from that
 * cache and never reaches the real backend. This only affects the gallery
 * preview; every placed widget on an actual dashboard still uses the app's
 * shared `QueryClientProvider` and fetches real org data. Re-mounting a
 * preview (e.g. reopening the gallery drawer) generates fresh random values.
 * The two param-gated widgets (evidence checklist / workpapers package) have
 * no seed to give — they just render their existing "select a run/control"
 * placeholder, same as an unfilled real dashboard.
 */
const WidgetPreview: React.FC<{ widget: WidgetDefinition }> = ({ widget }) => {
  const Component = widget.Component;

  const previewQueryClient = useMemo(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: Infinity,
          gcTime: Infinity,
          retry: false,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          networkMode: 'always',
        },
      },
    });
    const seed = getPreviewSeed(widget.widget_key);
    if (seed) {
      client.setQueryData(seed.queryKey, seed.getData());
    }
    return client;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.widget_key]);

  return (
    <div
      className="relative w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 pointer-events-none select-none"
      style={{ height: PREVIEW_HEIGHT_PX }}
      aria-hidden="true"
    >
      <div
        className="origin-top-left bg-white"
        style={{
          width: `${100 / PREVIEW_SCALE}%`,
          height: PREVIEW_HEIGHT_PX / PREVIEW_SCALE,
          transform: `scale(${PREVIEW_SCALE})`,
        }}
      >
        <div className="h-full w-full overflow-hidden p-3">
          <PreviewErrorBoundary category={widget.category}>
            <QueryClientProvider client={previewQueryClient}>
              <WidgetFrameProvider embedded>
                <Component />
              </WidgetFrameProvider>
            </QueryClientProvider>
          </PreviewErrorBoundary>
        </div>
      </div>
    </div>
  );
};

function genInstanceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `w_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Packs widgets top-to-bottom, filling the 12-column grid left-to-right within each "row band". */
function autoPlace(widgets: WidgetDefinition[]): WidgetInstance[] {
  let cursorX = 0;
  let cursorY = 0;
  let rowMaxH = 0;
  const placed: WidgetInstance[] = [];
  for (const widget of widgets) {
    const w = Math.min(widget.defaultSize.w, GRID_COLS);
    if (cursorX + w > GRID_COLS) {
      cursorX = 0;
      cursorY += rowMaxH;
      rowMaxH = 0;
    }
    placed.push({
      instance_id: genInstanceId(),
      widget_key: widget.widget_key,
      x: cursorX,
      y: cursorY,
      w,
      h: widget.defaultSize.h,
    });
    cursorX += w;
    rowMaxH = Math.max(rowMaxH, widget.defaultSize.h);
  }
  return placed;
}

/** Drops any instance whose widget_key no longer exists in the registry (renamed/removed widget). */
function sanitizeInstances(instances: WidgetInstance[]): WidgetInstance[] {
  return instances.filter((inst) => !!getWidgetDefinition(inst.widget_key));
}

/**
 * Key Metrics section column span — a denser 3-col grid than Charts, since
 * KPI-category widgets (gauges, headline panels, calendar strips) tend to
 * read fine narrower than a chart. Same saved-`w` width hint, mapped onto a
 * 3-col grid instead of 12. Literal class strings for the JIT scanner.
 */
function kpiColSpanClass(w: number): string {
  if (w >= 8) return 'lg:col-span-3';
  if (w >= 4) return 'lg:col-span-2';
  return 'lg:col-span-1';
}

/**
 * Charts section column span — a simple responsive 1/2-col grid; a widget
 * saved wide (>= 8 of 12 cols in edit mode) spans both columns, everything
 * else takes one. Literal class strings for the JIT scanner.
 */
function chartColSpanClass(w: number): string {
  return w >= 8 ? 'lg:col-span-2' : 'lg:col-span-1';
}

/**
 * Renders one preview-mode section's tiles — same standalone `WidgetCard`
 * chrome + category badge as before, just parameterized by which grid the
 * section uses (`colSpanClass`) so Key Metrics/Charts/Detailed Data can each
 * have their own natural-flow density.
 */
function renderPreviewTiles(insts: WidgetInstance[], colSpanClass: (w: number) => string): React.ReactNode[] {
  return insts
    .map((inst) => {
      const def = getWidgetDefinition(inst.widget_key);
      if (!def) return null;
      const Component = def.Component;
      return (
        <div key={inst.instance_id} className={`min-w-0 ${colSpanClass(inst.w)}`}>
          <div className="mb-1.5 flex justify-end">
            <CategoryBadge category={def.category} />
          </div>
          <Component />
        </div>
      );
    })
    .filter((node): node is React.ReactElement => node !== null);
}

/** Fixed reading-order IA for Preview mode, matching the persona dashboards: KPIs, then charts, then raw-data tables. */
const PREVIEW_SECTION_TITLES: Record<WidgetCategory, string> = {
  kpi: 'Key Metrics',
  chart: 'Charts',
  table: 'Detailed Data',
};

/** Above this many placed widgets, Preview mode splits into Overview / Raw Data tabs like the persona dashboards, rather than one long scroll. */
const PREVIEW_TABS_THRESHOLD = 6;

/**
 * Self-serve, customizable dashboard (Feature 5.3, wave 13). A user picks a
 * persona dashboard key, then adds/removes/repositions any report widget
 * from the full registry gallery — independent of which persona folder the
 * widget originally shipped under. Uses `react-grid-layout` for
 * drag-reposition + resize (installed cleanly against this CRA/craco setup,
 * no tsconfig to trip its bundled types).
 */
export const SelfServeDashboard: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState<PersonaKey>('controller');
  const [instances, setInstances] = useState<WidgetInstance[]>([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Preview is the safe, finished-dashboard-looking default; edit mode is an
  // explicit opt-in via the "Edit dashboard" button (problem: the grid used
  // to always render in edit mode, drag handles and all).
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  // Preview-only, client-side filter — widgets are heterogeneous and
  // self-fetching so a fully-wired slicer bar (like the persona pages) is
  // out of scope here; this is a genuinely-working substitute: it hides
  // placed widgets by title/category without pretending to filter any data.
  const [previewFilter, setPreviewFilter] = useState('');

  const layoutQuery = useDashboardLayoutQuery(selectedKey);
  const saveMutation = useSaveDashboardLayout(selectedKey);
  const resetMutation = useResetDashboardLayout(selectedKey);

  const dirtyRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingScrollIdRef = useRef<string | null>(null);

  // Hydrate local state whenever the persona key changes or fresh server data arrives.
  useEffect(() => {
    if (!layoutQuery.data) return;
    dirtyRef.current = false;
    setReadOnly(false);
    setSaveError(null);
    setMode('preview');
    setPreviewFilter('');
    const serverLayout = sanitizeInstances(layoutQuery.data.layout);
    if (layoutQuery.data.is_default && serverLayout.length === 0) {
      setInstances(autoPlace(getDefaultWidgetsForPersona(selectedKey)));
    } else {
      setInstances(serverLayout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, layoutQuery.data]);

  // Once a newly-added tile has actually rendered (post state-commit), scroll
  // it into view — reading DOM state from a ref set at add-time avoids racing
  // the async setState that puts the new instance in the grid.
  useEffect(() => {
    const pendingId = pendingScrollIdRef.current;
    if (!pendingId) return;
    pendingScrollIdRef.current = null;
    const el = document.getElementById(`widget-tile-${pendingId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [instances]);

  useEffect(
    () => () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    []
  );

  const scheduleSave = useCallback(
    (next: WidgetInstance[]) => {
      dirtyRef.current = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        if (!dirtyRef.current) return;
        saveMutation.mutate(next, {
          onSuccess: () => {
            dirtyRef.current = false;
            setSaveError(null);
          },
          onError: (err: any) => {
            const status = err?.response?.status;
            if (status === 403) {
              setReadOnly(true);
              setSaveError(
                resolve403ErrorMessage(
                  err,
                  'You do not have permission to save changes to this dashboard — showing a read-only view.'
                )
              );
            } else if (status === 401) {
              setReadOnly(true);
              setSaveError('You do not have permission to save changes to this dashboard — showing a read-only view.');
            } else {
              setSaveError('Failed to save your layout changes. They are kept locally for this session.');
            }
          },
        });
      }, SAVE_DEBOUNCE_MS);
    },
    [saveMutation]
  );

  const updateInstances = useCallback(
    (next: WidgetInstance[]) => {
      setInstances(next);
      scheduleSave(next);
    },
    [scheduleSave]
  );

  const handleAddWidget = useCallback(
    (widget: WidgetDefinition) => {
      const [placed] = autoPlace([widget]);
      // Append below the current lowest widget rather than overlapping at (0,0).
      const maxY = instances.reduce((m, inst) => Math.max(m, inst.y + inst.h), 0);
      const newInstance: WidgetInstance = { ...placed, x: 0, y: maxY };
      const next = [...instances, newInstance];
      updateInstances(next);

      toast.success(`Added ${widget.title}`);

      setHighlightedId(newInstance.instance_id);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlightedId(null), 1500);

      // Picked up by the effect above once `instances` actually re-renders
      // with the new tile in the DOM.
      pendingScrollIdRef.current = newInstance.instance_id;
    },
    [instances, updateInstances]
  );

  const handleRemoveWidget = useCallback(
    (instanceId: string) => {
      updateInstances(instances.filter((inst) => inst.instance_id !== instanceId));
    },
    [instances, updateInstances]
  );

  const handleGridLayoutChange = useCallback(
    (rglLayout: Layout[]) => {
      const byId = new Map(rglLayout.map((l) => [l.i, l]));
      const next = instances.map((inst) => {
        const l = byId.get(inst.instance_id);
        if (!l) return inst;
        return { ...inst, x: l.x, y: l.y, w: l.w, h: l.h };
      });
      updateInstances(next);
    },
    [instances, updateInstances]
  );

  const handleReset = useCallback(() => {
    dirtyRef.current = false;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    resetMutation.mutate(undefined, {
      onSuccess: () => {
        setSaveError(null);
        setInstances(autoPlace(getDefaultWidgetsForPersona(selectedKey)));
      },
      onError: (err: any) => {
        const status = err?.response?.status;
        if (status === 403) {
          setReadOnly(true);
          setSaveError(
            resolve403ErrorMessage(err, 'You do not have permission to reset this dashboard.')
          );
        } else if (status === 401) {
          setReadOnly(true);
          setSaveError('You do not have permission to reset this dashboard.');
        } else {
          setSaveError('Failed to reset the dashboard layout.');
        }
      },
    });
  }, [resetMutation, selectedKey]);

  const rglLayout: Layout[] = useMemo(
    () =>
      instances.map((inst) => {
        const def = getWidgetDefinition(inst.widget_key);
        return {
          i: inst.instance_id,
          x: inst.x,
          y: inst.y,
          w: inst.w,
          h: inst.h,
          minW: def?.minSize.w ?? 3,
          minH: def?.minSize.h ?? 2,
        };
      }),
    [instances]
  );

  const placedKeys = useMemo(() => new Set(instances.map((i) => i.widget_key)), [instances]);

  // Preview mode drops react-grid-layout entirely and lays widgets out in
  // natural document flow (like the persona dashboards) — reading order
  // comes from the saved layout's y-then-x position, same order the grid
  // visually implies in edit mode.
  const previewOrderedInstances = useMemo(
    () => [...instances].sort((a, b) => a.y - b.y || a.x - b.x),
    [instances]
  );

  // Client-side title/category filter applied on top of the saved reading
  // order — narrows what's shown without touching `instances` (the saved
  // layout) or refetching anything.
  const previewFilteredInstances = useMemo(() => {
    const q = previewFilter.trim().toLowerCase();
    if (!q) return previewOrderedInstances;
    return previewOrderedInstances.filter((inst) => {
      const def = getWidgetDefinition(inst.widget_key);
      if (!def) return false;
      return def.title.toLowerCase().includes(q) || def.category.includes(q);
    });
  }, [previewOrderedInstances, previewFilter]);

  // Same widgets, auto-organized into the persona-dashboard IA: KPIs first,
  // charts second, tables at the bottom — each group keeps the user's saved
  // (y, x) reading order, it's just re-bucketed by registry `category`.
  const previewGroups = useMemo(() => {
    const groups: Record<WidgetCategory, WidgetInstance[]> = { kpi: [], chart: [], table: [] };
    for (const inst of previewFilteredInstances) {
      const def = getWidgetDefinition(inst.widget_key);
      if (!def) continue;
      groups[def.category].push(inst);
    }
    return groups;
  }, [previewFilteredInstances]);

  // Match the persona dashboards' "tabs if the page would otherwise be one
  // long scroll" judgment call: only worth tabbing when there's an
  // Overview-ish portion (KPIs/charts) *and* a Raw Data portion (tables) to
  // separate — otherwise a single flowing section reads cleaner.
  const previewUseTabs =
    instances.length > PREVIEW_TABS_THRESHOLD &&
    previewGroups.table.length > 0 &&
    (previewGroups.kpi.length > 0 || previewGroups.chart.length > 0);

  const filteredByCategory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const groups: Record<WidgetCategory, WidgetDefinition[]> = { kpi: [], chart: [], table: [] };
    for (const widget of WIDGET_REGISTRY) {
      if (
        q &&
        !widget.title.toLowerCase().includes(q) &&
        !widget.widget_key.includes(q) &&
        !(widget.description ?? '').toLowerCase().includes(q) &&
        !widget.personas.some((p) => PERSONA_LABELS[p].toLowerCase().includes(q))
      ) {
        continue;
      }
      groups[widget.category].push(widget);
    }
    return groups;
  }, [search]);

  const galleryResultCount = useMemo(
    () => CATEGORY_ORDER.reduce((sum, cat) => sum + filteredByCategory[cat].length, 0),
    [filteredByCategory]
  );

  const isLoading = layoutQuery.isLoading;
  const isError = layoutQuery.isError;

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<LayoutGrid className="h-5 w-5" />}
          iconClassName="bg-teal-50 text-teal-600"
          title="My Dashboard"
          description="Pick, arrange, and resize any report widget from the catalog — it's yours to build"
          toolbar={
            <>
              <label htmlFor="dashboard-key-select" className="text-sm font-medium text-slate-600">
                Customizing
              </label>
              <select
                id="dashboard-key-select"
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value as PersonaKey)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {PERSONA_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {PERSONA_LABELS[key]} dashboard
                  </option>
                ))}
              </select>

              <span className="mx-1 hidden h-5 w-px bg-slate-200 sm:inline-block" aria-hidden />

              {mode === 'preview' ? (
                <button
                  type="button"
                  onClick={() => setMode('edit')}
                  disabled={readOnly}
                  className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Pencil className="h-4 w-4" />
                  Edit dashboard
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setGalleryOpen(true)}
                    disabled={readOnly}
                    className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-4 w-4" />
                    Add widget
                  </button>

                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={readOnly || resetMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset to default
                  </button>

                  <span className="mx-1 hidden h-5 w-px bg-slate-200 sm:inline-block" aria-hidden />

                  <button
                    type="button"
                    onClick={() => setMode('preview')}
                    className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-slate-800"
                  >
                    <Check className="h-4 w-4" />
                    Done
                  </button>
                </>
              )}
            </>
          }
        />

        {mode === 'edit' && !readOnly && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-teal-100 bg-teal-50/70 px-4 py-2.5 text-xs font-medium text-teal-700">
            <Info className="h-3.5 w-3.5 shrink-0" />
            Drag a tile to move it · drag its bottom-right corner to resize · click × to remove a widget
          </div>
        )}

        {readOnly && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            You&apos;re viewing a read-only copy of this dashboard — you don&apos;t have permission to save changes here.
          </div>
        )}
        {!readOnly && saveError && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {saveError}
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="analytics-skeleton h-4 w-1/3 mb-4" />
                <div className="analytics-skeleton h-32 w-full" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-6 text-center text-sm text-rose-600">
            Failed to load this dashboard&apos;s layout.
          </div>
        ) : instances.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-20 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal-50">
              <LayoutGrid className="h-7 w-7 text-teal-500" />
            </div>
            <p className="text-base font-semibold text-slate-700 mb-1">Add widgets to build your dashboard</p>
            <p className="text-sm text-slate-400 mb-5">
              Browse the catalog of {WIDGET_REGISTRY.length} report widgets and pick the ones that matter to you.
            </p>
            <button
              type="button"
              onClick={() => {
                setMode('edit');
                setGalleryOpen(true);
              }}
              disabled={readOnly}
              className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Add your first widget
            </button>
          </div>
        ) : mode === 'preview' ? (
          // Preview: same natural-flow rendering as before (NOT
          // react-grid-layout — no fixed tile heights, no inner scrollbars,
          // each widget its own standalone `WidgetCard` at full content
          // height, page scrolls as a whole) but now auto-organized into the
          // persona-dashboard IA: widgets are grouped by registry `category`
          // (keeping the saved y-then-x order within each group) and
          // rendered as titled sections in a fixed KPIs -> charts -> tables
          // order, tabbed into Overview/Raw Data once the dashboard gets
          // busy — same judgment call the persona pages make.
          <div>
            <div className="mb-6 max-w-xs">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden />
                <input
                  type="text"
                  value={previewFilter}
                  onChange={(e) => setPreviewFilter(e.target.value)}
                  placeholder="Filter your widgets by title or category…"
                  className="w-full rounded-md border border-slate-300 pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {previewFilteredInstances.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-16 text-center text-sm text-slate-400">
                No widgets match &ldquo;{previewFilter}&rdquo;.
              </div>
            ) : previewUseTabs ? (
              <AnalyticsTabs
                tabs={[
                  {
                    key: 'overview',
                    label: 'Overview',
                    icon: <GaugeCircle className="h-4 w-4" />,
                    content: (
                      <div className="grid grid-cols-1 gap-8">
                        {previewGroups.kpi.length > 0 && (
                          <section>
                            <SectionHeader title={PREVIEW_SECTION_TITLES.kpi} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                              {renderPreviewTiles(previewGroups.kpi, kpiColSpanClass)}
                            </div>
                          </section>
                        )}
                        {previewGroups.chart.length > 0 && (
                          <section>
                            <SectionHeader title={PREVIEW_SECTION_TITLES.chart} />
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {renderPreviewTiles(previewGroups.chart, chartColSpanClass)}
                            </div>
                          </section>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'raw',
                    label: 'Raw Data',
                    icon: <Table2 className="h-4 w-4" />,
                    content: (
                      <section>
                        <SectionHeader title={PREVIEW_SECTION_TITLES.table} />
                        <div className="grid grid-cols-1 gap-6">
                          {renderPreviewTiles(previewGroups.table, () => '')}
                        </div>
                      </section>
                    ),
                  },
                ]}
              />
            ) : (
              <div className="grid grid-cols-1 gap-8">
                {previewGroups.kpi.length > 0 && (
                  <section>
                    <SectionHeader title={PREVIEW_SECTION_TITLES.kpi} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {renderPreviewTiles(previewGroups.kpi, kpiColSpanClass)}
                    </div>
                  </section>
                )}
                {previewGroups.chart.length > 0 && (
                  <section>
                    <SectionHeader title={PREVIEW_SECTION_TITLES.chart} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {renderPreviewTiles(previewGroups.chart, chartColSpanClass)}
                    </div>
                  </section>
                )}
                {previewGroups.table.length > 0 && (
                  <section>
                    <SectionHeader title={PREVIEW_SECTION_TITLES.table} />
                    <div className="grid grid-cols-1 gap-6">
                      {renderPreviewTiles(previewGroups.table, () => '')}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        ) : (
          <ResponsiveGridLayout
            className="analytics-grid layout analytics-grid-edit"
            layout={rglLayout}
            cols={GRID_COLS}
            rowHeight={ROW_HEIGHT}
            margin={[16, 16]}
            isDraggable={!readOnly}
            isResizable={!readOnly}
            resizeHandles={['se']}
            onLayoutChange={handleGridLayoutChange}
            draggableHandle=".widget-drag-handle"
          >
            {instances.map((inst) => {
              const def = getWidgetDefinition(inst.widget_key);
              if (!def) return null;
              const Component = def.Component;
              return (
                <div
                  key={inst.instance_id}
                  id={`widget-tile-${inst.instance_id}`}
                  className={`flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm ${
                    highlightedId === inst.instance_id ? 'analytics-tile-flash' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/80 shrink-0 widget-drag-handle analytics-widget-drag-handle">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <GripVertical className="h-3.5 w-3.5 shrink-0 text-slate-300" aria-hidden />
                      <span className="truncate text-xs font-semibold text-slate-600">{def.title}</span>
                      <CategoryBadge category={def.category} className="hidden sm:inline-flex" />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveWidget(inst.instance_id)}
                      disabled={readOnly}
                      title="Remove widget"
                      className="text-slate-400 hover:text-rose-500 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="analytics-scroll flex-1 min-h-0 overflow-auto p-3">
                    <WidgetFrameProvider embedded>
                      <Component />
                    </WidgetFrameProvider>
                  </div>
                </div>
              );
            })}
          </ResponsiveGridLayout>
        )}
      </div>

      {galleryOpen && (
        <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label="Widget gallery">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setGalleryOpen(false)} />
          <div className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Widget gallery</h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  {WIDGET_REGISTRY.length} report widgets — pick any to add to &ldquo;{PERSONA_LABELS[selectedKey]}&rdquo;
                </p>
              </div>
              <button
                type="button"
                onClick={() => setGalleryOpen(false)}
                className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Close widget gallery"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="shrink-0 border-b border-slate-100 px-5 py-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search widgets by name, description, or persona…"
                  className="w-full rounded-md border border-slate-300 pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  autoFocus
                />
              </div>
            </div>

            <div className="analytics-scroll flex-1 overflow-y-auto px-5 py-5 space-y-7">
              {galleryResultCount === 0 && <p className="text-sm text-slate-400 text-center py-10">No widgets match your search.</p>}
              {CATEGORY_ORDER.map((category) => {
                const widgets = filteredByCategory[category];
                if (widgets.length === 0) return null;
                return (
                  <div key={category}>
                    <div className="mb-3 flex items-center gap-2">
                      <CategoryBadge category={category} />
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {CATEGORY_LABELS[category]}s · {widgets.length}
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {widgets.map((widget) => {
                        const already = placedKeys.has(widget.widget_key);
                        return (
                          <div
                            key={widget.widget_key}
                            className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 transition-colors hover:border-teal-300 hover:shadow-sm"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-900">{widget.title}</p>
                              {widget.description ? (
                                <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-slate-500">{widget.description}</p>
                              ) : null}
                            </div>
                            <WidgetPreview widget={widget} />
                            <button
                              type="button"
                              onClick={() => handleAddWidget(widget)}
                              disabled={readOnly}
                              className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                already
                                  ? 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                                  : 'bg-teal-600 text-white hover:bg-teal-700'
                              }`}
                            >
                              {already ? (
                                <>
                                  <Check className="h-3.5 w-3.5" /> Added — add another
                                </>
                              ) : (
                                <>
                                  <Plus className="h-3.5 w-3.5" /> Add to dashboard
                                </>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelfServeDashboard;
